// src/pages/finance/EntryFeeInvoices.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Divider,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import PendingIcon from "@mui/icons-material/PendingActions";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import { exportEntryFeeInvoices, fetchEntryFeeInvoices } from "../services/entryFeeInvoiceService";
import { useTranslation } from "react-i18next";

const brand = "#800b33";
const brandHover = "#6c0a2b";

// ✅ CURRENCY FORMAT
const formatMoney = (n, locale = "en-TZ") => `TZS ${new Intl.NumberFormat(locale).format(n || 0)}`;
const normalizeStatus = (s) => (s || "").toUpperCase();
const adaptInvoice = (inv) => ({
  id: inv.invoice_id || inv.id,
  group: inv.group_name || inv.group || inv.group_title,
  member: inv.member_name || inv.member,
  amount: inv.amount,
  status: normalizeStatus(inv.status),
  date: inv.created_at || inv.date,
});

export default function EntryFeeInvoicePage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw-TZ" : "en-TZ";
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // ✅ FETCH DATA
  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const params = {};
        if (statusFilter !== "ALL") params.status = statusFilter;
        if (search.trim()) params.search = search.trim();
        const data = await fetchEntryFeeInvoices(params);
        if (!active) return;
        const list = data?.results || data || [];
        setInvoices(Array.isArray(list) ? list.map(adaptInvoice) : []);
      } catch (err) {
        if (!active) return;
        setError(err.message || t("Failed to load entry fee invoices."));
        setInvoices([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [statusFilter, search, refreshKey]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const blob = await exportEntryFeeInvoices(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `entry_fee_invoices_${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || t("Failed to export invoices."));
    } finally {
      setExporting(false);
    }
  };

  // ✅ FILTERED DATA
  const filteredInvoices = useMemo(() => {
    const term = search.toLowerCase();
    return invoices.filter((inv) => {
      const matchesStatus = statusFilter === "ALL" || normalizeStatus(inv.status) === statusFilter;
      const matchesSearch =
        (inv.group || "").toLowerCase().includes(term) ||
        (inv.member || "").toLowerCase().includes(term) ||
        (inv.id || "").toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [statusFilter, search, invoices]);

  // ✅ STATUS CHIP
  const getStatusChip = (status) => {
    if (status === "PAID")
      return <Chip icon={<PaidIcon />} label={t("PAID")} color="success" />;
    if (status === "PENDING")
      return <Chip icon={<PendingIcon />} label={t("PENDING")} color="warning" />;
    return <Chip icon={<CancelIcon />} label={t("FAILED")} color="error" />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* ✅ HEADER */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Entry Fee Invoices")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Track all group entry fee payments and their current status")}
        </Typography>
      </Box>

      {/* ✅ FILTER BAR */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t("Search by invoice, member or group...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("Filter Status")}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">{t('All')}</MenuItem>
                <MenuItem value="PAID">{t('PAID')}</MenuItem>
                <MenuItem value="PENDING">{t('PENDING')}</MenuItem>
                <MenuItem value="FAILED">{t('FAILED')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exporting || loading}
                sx={{
                  bgcolor: brand,
                  "&:hover": { bgcolor: brandHover },
                  height: "56px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {exporting ? t("Exporting...") : t("Export Report")}
              </Button>
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="text"
                onClick={() => setRefreshKey((k) => k + 1)}
                disabled={loading}
                sx={{ color: brand, textTransform: "none", fontWeight: 700 }}
              >
                {t("Refresh")}
              </Button>
            </Grid>
          </Grid>
          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ✅ INVOICES TABLE */}
      <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            {t("All Entry Fee Invoices")}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f9fafb" }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t("Invoice No")}</TableCell>
                  <TableCell>{t("Group")}</TableCell>
                  <TableCell>{t("Member")}</TableCell>
                  <TableCell>{t("Amount")}</TableCell>
                  <TableCell>{t("Status")}</TableCell>
                  <TableCell>{t("Date")}</TableCell>
                  <TableCell align="right">{t("Action")}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredInvoices.length > 0 ? (
                  filteredInvoices.map((inv, i) => (
                    <TableRow key={inv.id || i} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{inv.id}</TableCell>
                      <TableCell>{inv.group}</TableCell>
                      <TableCell>{inv.member}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {formatMoney(inv.amount, locale)}
                      </TableCell>
                      <TableCell>{getStatusChip(inv.status)}</TableCell>
                      <TableCell>{inv.date}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ReceiptLongIcon />}
                          sx={{
                            color: brand,
                            borderColor: brand,
                            "&:hover": {
                              borderColor: brandHover,
                              color: brandHover,
                            },
                          }}
                        >
                          {t("View")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {t("No invoices found.")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
